import { TableColumn } from '../types/table-column.type';
import { DataTableColumnDirective } from '../components/columns/column.directive';
import { camelCase, deCamelCase } from './camel-case';
import { id } from './id';
import { getterForProp } from './column-prop-getters';

/**
 * Sets the column defaults
 */
export function setColumnDefaults(columns: Array<TableColumn>): void {
  if (!columns) {return;}

  // Only one column should hold the tree view
  // Thus if multiple columns are provided with
  // isTreeColumn as true we take only the first one
  let treeColumnFound = false;

  for (const column of columns) {
    if (!column.$$id) {
      column.$$id = id();
    }

    // prop can be numeric; zero is valid not a missing prop
    // translate name => prop
    if (valueIsNullOrUndefined(column.prop) && column.name) {
      column.prop = camelCase(column.name);
    }

    if (!column.$$valueGetter) {
      column.$$valueGetter = getterForProp(column.prop);
    }

    // format props if no name passed
    if (!valueIsNullOrUndefined(column.prop) && valueIsNullOrUndefined(column.name)) {
      column.name = deCamelCase(String(column.prop));
    }

    if (valueIsNullOrUndefined(column.prop) && valueIsNullOrUndefined(column.name)) {
      column.name = ''; // Fixes IE and Edge displaying `null`
    }

    if (!Object.prototype.hasOwnProperty.call(column, 'resizeable')) {
      column.resizeable = true;
    }

    if (!Object.prototype.hasOwnProperty.call(column, 'sortable')) {
      column.sortable = true;
    }

    if (!Object.prototype.hasOwnProperty.call(column, 'draggable')) {
      column.draggable = true;
    }

    if (!Object.prototype.hasOwnProperty.call(column, 'canAutoResize')) {
      column.canAutoResize = true;
    }

    if (!Object.prototype.hasOwnProperty.call(column, 'width')) {
      column.width = 150;
    }

    if (!Object.prototype.hasOwnProperty.call(column, 'isTreeColumn')) {
      column.isTreeColumn = false;
    } else {
      if (column.isTreeColumn && !treeColumnFound) {
        // If the first column with isTreeColumn is true found
        // we mark that treeCoulmn is found
        treeColumnFound = true;
      } else {
        // After that isTreeColumn property for any other column
        // will be set as false
        column.isTreeColumn = false;
      }
    }
  }
}

export function valueIsNullOrUndefined<T>(value: T | null | undefined): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * List of `input()` signal properties on `DataTableColumnDirective` that need
 * to be unwrapped (invoked) when projecting the directive into a plain column
 * object. Keep in sync with the directive declaration.
 */
const SIGNAL_INPUT_PROPS: Array<keyof DataTableColumnDirective> = [
  'name',
  'prop',
  'frozenLeft',
  'frozenRight',
  'flexGrow',
  'resizeable',
  'comparator',
  'pipe',
  'sortable',
  'rowheader',
  'draggable',
  'canAutoResize',
  'minWidth',
  'width',
  'maxWidth',
  'checkboxable',
  'headerCheckboxable',
  'headerClass',
  'cellClass',
  'isTreeColumn',
  'treeLevelIndent'
];

/**
 * Translates templates definitions to objects.
 *
 * Reads each known signal `input()` on the directive and copies the unwrapped
 * value to a plain column object so downstream helpers (sort, render, pin,
 * width calculation) see real values rather than `InputSignal` functions.
 */
export function translateTemplates(templates: Array<DataTableColumnDirective>): Array<TableColumn> {
  const result: Array<TableColumn> = [];
  for (const temp of templates) {
    const col: TableColumn = {};

    for (const prop of SIGNAL_INPUT_PROPS) {
      const accessor = (temp as unknown as Record<string, unknown>)[prop];
      if (typeof accessor === 'function') {
        const value = (accessor as () => unknown)();
        if (value !== undefined) {
          (col as Record<string, unknown>)[prop] = value;
        }
      }
    }

    if (temp.headerTemplate) {
      col.headerTemplate = temp.headerTemplate;
    }

    if (temp.cellTemplate) {
      col.cellTemplate = temp.cellTemplate;
    }

    if (temp.treeToggleTemplate) {
      col.treeToggleTemplate = temp.treeToggleTemplate;
    }

    const summaryFunc = temp.summaryFunc();
    if (summaryFunc) {
      col.summaryFunc = summaryFunc;
    }

    const summaryTemplate = temp.summaryTemplate();
    if (summaryTemplate) {
      col.summaryTemplate = summaryTemplate;
    }

    result.push(col);
  }

  return result;
}
